import {
  getGetNodoPortaleQueryKey,
  getGetNodoQueryKey,
  ManutenzioneDto,
  NodoDto,
  useUpdateManutenzioni
} from "@/api";
import FileField from "@/components/FileField";
import Button from "@/elements/Button";
import { withOpeningReset } from "@/utils/withOpeningReset";
import { FormHelperText, InputLabel } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { DateTimeField } from "@mui/x-date-pickers";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import DescrizioneManutenzionePicker from "../DescrizioneManutenzionePicker";
import { SmallLoading } from "../Loading";
import { portaleConfig } from "@/portaliConfig";

function ManutenzioneDialog({
  open = false,
  onClose,
  nodo,
  manutenzione,
}: {
  open?: boolean;
  onClose?: () => void;
  nodo: NodoDto;
  manutenzione?: Partial<ManutenzioneDto> | null;
}) {
  const { t } = useTranslation();

  const handleClose = () => {
    onClose?.();
  };

  const {
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    resetField,
    control,
  } = useForm<ManutenzioneDto>({
    defaultValues: manutenzione || {},
  });

  const { mutateAsync, isLoading } = useUpdateManutenzioni();

  const queryClient = useQueryClient();

  const onSubmit = async (data: ManutenzioneDto) => {
    try {
      await mutateAsync({
        id: nodo.id,
        data: [
          {
            id: manutenzione?.id,
            changeType: manutenzione?.id ? "updated" : "added",
            entity: data,
          },
        ],
      });
      await queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id),
      });
      toast.success(t("manutenzioneDialog.toast.salvata"));
      onClose?.();
    } catch (err) {}
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {manutenzione?.id
          ? t("manutenzioneDialog.titolo.modifica")
          : t("manutenzioneDialog.titolo.crea")}
      </DialogTitle>
      <DialogContent>
        <InputLabel htmlFor="crea-filtrazione-data">
          {t("manutenzioneDialog.campi.data")}
        </InputLabel>
        <Controller
          name="data"
          control={control}
          rules={{ required: t("manutenzioneDialog.errori.data") }}
          render={({ field, fieldState: { error } }) => {
            const { value, name, onBlur, onChange, ref } = field;
            const m =
              typeof value === "string"
                ? moment(value)
                : moment.isMoment(value)
                ? value
                : null;
            return (
              <>
                <DateTimeField
                  ref={ref}
                  format="DD/MM/YYYY"
                  name={name}
                  onBlur={onBlur}
                  onChange={(value) => onChange(value?.format("YYYY-MM-DD"))}
                  value={m}
                  fullWidth
                />
                {error && (
                  <FormHelperText error>{error?.message}</FormHelperText>
                )}
              </>
            );
          }}
        />

        <InputLabel htmlFor="crea-manutenzione-descrizione">
          {t("manutenzioneDialog.campi.descrizione")}
        </InputLabel>
        <Controller
          name="descrizione"
          control={control}
          rules={{ required: t("manutenzioneDialog.errori.descrizione") }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <DescrizioneManutenzionePicker
                value={value}
                onChange={(e, v) => onChange(v)}
              />
              {error && <FormHelperText error>{error?.message}</FormHelperText>}
            </>
          )}
        />

        <InputLabel htmlFor="crea-manutenzione-rapportino">
          {t("manutenzioneDialog.campi.rapportino")}
        </InputLabel>
        <Controller
          name="fileRapportino"
          control={control}
          rules={{ required: t("manutenzioneDialog.errori.file") }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <FileField
                value={value}
                onChange={(f) => {
                  onChange(f);
                  if (f?.id) {
                    setValue("fileRapportinoId", f?.id);
                  } else {
                    resetField("fileRapportinoId");
                  }
                }}
              />
              {error && <FormHelperText error>{error?.message}</FormHelperText>}
            </>
          )}
        />
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {manutenzione?.id
            ? t("manutenzioneDialog.azioni.salva")
            : t("manutenzioneDialog.azioni.crea")}
        </Button>
        <Button
          variant="contained"
          color="light"
          disabled={isLoading}
          onClick={handleClose}
        >
          {t("azioni.annulla")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(ManutenzioneDialog);
